/**
 * Helpers for Vendor Lists tab — member extraction tolerant of API shape drift.
 * Filter + mapper modules share these so future backend contracts land in one place.
 */

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidProviderId(value) {
  return GUID_PATTERN.test(String(value || "").trim());
}

/** Resolve provider id from Discovery / My Vendors / API-shaped vendor objects. */
export function resolveVendorProviderId(vendor = {}) {
  const raw =
    vendor.id ??
    vendor.Id ??
    vendor.provider_id ??
    vendor.providerId ??
    "";
  return String(raw).trim();
}

/** Build a POST /Lists/.../Members payload row from any vendor card shape. */
export function buildVendorListMemberPayload(vendor = {}) {
  const id = resolveVendorProviderId(vendor);
  if (!isValidProviderId(id)) return null;

  const normalized = normalizeVendorListMember({ ...vendor, id });
  let logo = normalized.logoUrl;
  if (logo && logo.length > 2048) logo = logo.slice(0, 2048);

  let name = normalized.name;
  if (name.length > 512) name = name.slice(0, 512);

  return {
    provider_id: id,
    name,
    logo,
    service_categories: normalized.serviceCategories,
  };
}

/**
 * Search (`/Providers/Filters/Search`) does not return capabilities. When a member
 * payload has no service_categories, fill them from GET /Providers/{id}/Basic-Profile.
 */
export async function hydrateVendorListMemberPayloads(vendors = [], fetchProfile) {
  const payloads = vendors.map((vendor) => buildVendorListMemberPayload(vendor)).filter(Boolean);
  if (typeof fetchProfile !== "function") return payloads;

  const missing = payloads.filter((payload) => !payload.service_categories?.length);
  if (!missing.length) return payloads;

  const fetched = await Promise.all(
    missing.map(async (payload) => {
      try {
        const profile = await fetchProfile(payload.provider_id);
        return [payload.provider_id, normalizeMemberServiceNames(profile || {})];
      } catch {
        return [payload.provider_id, []];
      }
    })
  );
  const byId = new Map(fetched);

  return payloads.map((payload) => {
    if (payload.service_categories?.length) return payload;
    const names = byId.get(payload.provider_id) || [];
    return names.length ? { ...payload, service_categories: names } : payload;
  });
}

/** Members of an existing list, shaped for createList / duplicate-via-create. */
export function vendorsFromListForCreate(list = {}) {
  return extractVendorListMembers(list)
    .filter((member) => isValidProviderId(member.providerId))
    .map((member) => ({
      id: member.providerId,
      name: member.name,
      logoUrl: member.logoUrl,
      serviceCategories: member.serviceCategories || [],
    }));
}

function normalizeMemberServiceNames(member = {}) {
  const names = new Set();
  const push = (value) => {
    const trimmed = String(value || "").trim();
    if (trimmed) names.add(trimmed);
  };

  (member.serviceCategories || member.service_categories || []).forEach(push);
  (member.services || []).forEach((entry) => {
    if (typeof entry === "string") push(entry);
    else push(entry?.rfi_name || entry?.name);
  });
  (member.provider_capabilities || member.capabilities || []).forEach((cap) => {
    push(cap?.rfi_name || cap?.name);
  });

  return Array.from(names);
}

/** Normalize a list member row from API or prototype payloads. */
export function normalizeVendorListMember(raw = {}) {
  const providerId = raw.provider_id ?? raw.providerId ?? raw.id ?? raw.Id ?? null;
  const name = raw.name || raw.vendor_name || raw.provider_name || "";
  const logoUrl = raw.logo || raw.logo_url || raw.logoUrl || null;
  return {
    providerId: providerId != null ? String(providerId) : null,
    name: String(name).trim(),
    logoUrl: logoUrl ? String(logoUrl) : null,
    serviceCategories: normalizeMemberServiceNames(raw),
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    dateAdded: raw.date_added ?? raw.dateAdded ?? null,
    addedByName: raw.added_by_name ?? raw.addedByName ?? null,
    sortOrder: raw.sort_order ?? raw.sortOrder ?? null,
  };
}

/** Members on a vendor list (supports legacy `vendorMetadata` alias). */
export function extractVendorListMembers(list = {}) {
  const rows =
    list.members ||
    list.vendorMetadata ||
    list.vendor_metadata ||
    list.vendors ||
    [];
  if (!Array.isArray(rows)) return [];
  return rows.map(normalizeVendorListMember).filter((m) => m.name || m.providerId);
}

export function collectVendorNamesFromList(list = {}) {
  return extractVendorListMembers(list)
    .map((m) => m.name)
    .filter(Boolean);
}

export function collectServiceNamesFromList(list = {}) {
  const names = new Set();
  extractVendorListMembers(list).forEach((member) => {
    member.serviceCategories.forEach((service) => names.add(service));
  });
  return Array.from(names);
}

/**
 * Token is local state and starts null on every mount. Until it resolves the
 * lists query is disabled, isLoading is false, and user lists are missing —
 * redirecting here would bounce custom lists back to Vendor Lists.
 */
export function isVendorListDetailLookupPending({
  decoded = null,
  tokenError = null,
  listsLoading = false,
} = {}) {
  const tokenLoading = !decoded && !tokenError;
  return Boolean(tokenLoading || listsLoading);
}
